/**
 * ContratsAnnuaire (label "Contrats") — Table dense BE-TAYE 1:1 :
 *   - Filtres multi-select (type/produit/vendeur/revendeur/leaser/client/etat/statut_resil/année/gestion interne)
 *   - Colonnes configurables (afficher/masquer/réordonner drag-drop)
 *   - Vues sauvegardées (built-in + custom)
 *   - Sélection multiple + Export Excel (xlsx)
 *   - Import Excel + création/édition via ContractFormModal
 *   - Pagination 25/50/100
 *   - Mobile card view distinct
 *   - Étape GED colorée (vert/orange/rouge + compteur)
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search, FileText, Filter, ChevronLeft, ChevronRight, Eye, Upload,
  Plus, MoreVertical, Pencil, Trash2, ArrowUpAZ, ArrowDownAZ, ArrowUpDown,
  ChevronDown, FileSpreadsheet, X, CheckSquare, GripVertical, RefreshCw,
  Sparkles,
} from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "sonner";

import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import ColumnConfigPopover, {
  loadColumnsConfig, saveColumnsConfig, reorderColumns, DEFAULT_COLUMNS,
} from "@/components/ColumnConfigPopover";
import ContractViewsSelector, {
  applyViewToColumns, loadActiveViewId, saveActiveViewId,
} from "@/components/ContractViewsSelector";
import ImportContractsModal from "@/components/SmartImportModal";
import ContractFormModal from "@/components/ContractFormModal";
import ContractSemanticSearchDialog from "@/components/ContractSemanticSearchDialog";
import BulkDeleteContractsDialog from "@/components/BulkDeleteContractsDialog";

const fmtEUR = (v) => (v == null ? "-" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" }) : "-");

const STATUS_BADGE_CFG = {
  active: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Actif" },
  expiring_soon: { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "Expire bientôt" },
  expired: { cls: "bg-red-100 text-red-700 border-red-200", label: "Expiré" },
  cancelled: { cls: "bg-slate-200 text-slate-700 border-slate-300", label: "Résilié" },
};

const getStatusBadge = (c) => {
  const st = c.statut_resiliation || c.resiliation_status;
  let base;
  if (st === "resiliation_en_cours" || st === "en_cours")
    base = <Badge className="bg-orange-500 text-white border-orange-600" data-testid="badge-resiliation-en-cours">Résiliation en cours</Badge>;
  else if (st === "resilier" || st === "resilie")
    base = <Badge className="bg-slate-500 text-white border-slate-600" data-testid="badge-resilie">Résilié</Badge>;
  else {
    const cfg = STATUS_BADGE_CFG[c.status] || { cls: "bg-slate-100 text-slate-600 border-slate-200", label: c.status || "-" };
    base = <Badge className={cfg.cls} variant="outline">{cfg.label}</Badge>;
  }
  const rec = c.reconduction_state;
  let recBadge = null;
  if (rec === "en_reconduction")
    recBadge = <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-[10px]" title="Reconduit tacitement">🔁 Reconduit</Badge>;
  else if (rec === "reconduction_a_confirmer")
    recBadge = <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]" title="Reconduction potentielle">⚠️ À confirmer</Badge>;
  else if (c.tacite_reconduction || c.tacit_renewal)
    recBadge = <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">Tacite</Badge>;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">{base}{recBadge}</span>
  );
};

const getTypeBadge = (type) => {
  if (!type) return <span className="text-slate-400">-</span>;
  const colors = {
    "SAUVEGARDE": "bg-emerald-100 text-emerald-700 border-emerald-200",
    "CYBERSECURITE": "bg-cyan-100 text-cyan-700 border-cyan-200",
    "CYBERSÉCURITÉ": "bg-cyan-100 text-cyan-700 border-cyan-200",
  };
  return <Badge className={colors[type.toUpperCase()] || "bg-slate-100 text-slate-700 border-slate-200"}>{type}</Badge>;
};

const EtatIndicator = ({ etat }) => {
  if (!etat) return <span className="text-slate-400">-</span>;
  const cnt = etat.count || 0;
  const dot = {
    green: "bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.5)]",
    orange: "bg-amber-500 shadow-[0_0_8px_2px_rgba(245,158,11,0.5)]",
    red: "bg-rose-500 shadow-[0_0_8px_2px_rgba(244,63,94,0.5)]",
  }[etat.color] || "bg-slate-400";
  const badgeCls = {
    green: "bg-emerald-100 text-emerald-700",
    orange: "bg-amber-100 text-amber-700",
    red: "bg-rose-100 text-rose-700",
  }[etat.color] || "bg-slate-100 text-slate-500";
  return (
    <div className="inline-flex items-center gap-2">
      <div className={`w-3.5 h-3.5 rounded-full transition-all ${dot}`} />
      {cnt > 0 && <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${badgeCls}`}>{cnt}</span>}
    </div>
  );
};

const ETAT_OPTIONS = [
  { value: "green", label: "Complet", color: "bg-emerald-500" },
  { value: "orange", label: "En cours", color: "bg-amber-500" },
  { value: "red", label: "Vide", color: "bg-rose-500" },
];
const RESILIATION_OPTIONS = [
  { value: "resiliation_en_cours", label: "Résiliation en cours", color: "bg-orange-500" },
  { value: "resilier", label: "Résilié", color: "bg-slate-500" },
];

export default function ContratsAnnuaire({ embedded = false } = {}) {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canWrite = hasPermission("contracts.write");

  const [contracts, setContracts] = useState([]);
  const [filtersData, setFiltersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalGestionInterne, setTotalGestionInterne] = useState(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);

  // Filters
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState([]);
  const [etatFilter, setEtatFilter] = useState([]);
  const [resiliationFilter, setResiliationFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [produitFilter, setProduitFilter] = useState([]);
  const [vendeurFilter, setVendeurFilter] = useState([]);
  const [revendeurFilter, setRevendeurFilter] = useState([]);
  const [leaserFilter, setLeaserFilter] = useState([]);
  const [yearFilter, setYearFilter] = useState("all");
  const [gestionInterneFilter, setGestionInterneFilter] = useState("all");

  // Modals
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [showSemSearch, setShowSemSearch] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Selection
  const [selectedContracts, setSelectedContracts] = useState(new Set());
  // Sort
  const [sortOrder, setSortOrder] = useState(null);
  // Columns
  const [columns, setColumns] = useState(() => loadColumnsConfig());
  const [activeViewId, setActiveViewId] = useState(() => loadActiveViewId());
  const [draggedColId, setDraggedColId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);

  const handleSelectView = (view) => {
    const nc = applyViewToColumns(DEFAULT_COLUMNS, view);
    setColumns(nc); saveColumnsConfig(nc);
    setActiveViewId(view.id); saveActiveViewId(view.id);
  };

  const handleHeaderDragStart = (e, colId) => {
    setDraggedColId(colId); e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", colId);
  };
  const handleHeaderDragOver = (e, colId) => {
    e.preventDefault(); e.dataTransfer.dropEffect = "move";
    if (colId !== draggedColId) setDragOverColId(colId);
  };
  const handleHeaderDragLeave = () => setDragOverColId(null);
  const handleHeaderDrop = (e, dropColId) => {
    e.preventDefault();
    if (!draggedColId || draggedColId === dropColId) {
      setDraggedColId(null); setDragOverColId(null); return;
    }
    const dragIndex = columns.findIndex((c) => c.id === draggedColId);
    const dropIndex = columns.findIndex((c) => c.id === dropColId);
    const nc = reorderColumns(columns, dragIndex, dropIndex);
    setColumns(nc); saveColumnsConfig(nc);
    setDraggedColId(null); setDragOverColId(null);
  };
  const handleHeaderDragEnd = () => { setDraggedColId(null); setDragOverColId(null); };

  const fetchFilters = useCallback(async () => {
    try {
      const r = await api.get("/contracts/filters");
      setFiltersData(r.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.append("search", search);
      if (typeFilter.length) p.append("type", typeFilter.join(","));
      if (etatFilter.length) p.append("etat", etatFilter.join(","));
      if (resiliationFilter.length) p.append("statut_resiliation", resiliationFilter.join(","));
      if (clientFilter.length) p.append("client", clientFilter.join(","));
      if (produitFilter.length) p.append("produit", produitFilter.join(","));
      if (vendeurFilter.length) p.append("vendeur", vendeurFilter.join(","));
      if (revendeurFilter.length) p.append("revendeur", revendeurFilter.join(","));
      if (leaserFilter.length) p.append("leaser", leaserFilter.join(","));
      if (yearFilter && yearFilter !== "all") p.append("year", yearFilter);
      if (gestionInterneFilter && gestionInterneFilter !== "all") p.append("gestion_interne", gestionInterneFilter);
      p.append("limit", perPage); p.append("skip", page * perPage);
      const r = await api.get(`/contracts?${p.toString()}`);
      setContracts(r.data.contracts || []);
      setTotal(r.data.total || 0);
      setTotalGestionInterne(r.data.total_gestion_interne ?? null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [search, typeFilter, etatFilter, resiliationFilter, clientFilter, produitFilter,
      vendeurFilter, revendeurFilter, leaserFilter, yearFilter, gestionInterneFilter,
      page, perPage]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const resetFilters = () => {
    setSearch(""); setTypeFilter([]); setEtatFilter([]); setResiliationFilter([]);
    setClientFilter([]); setProduitFilter([]); setVendeurFilter([]); setRevendeurFilter([]);
    setLeaserFilter([]); setYearFilter("all"); setGestionInterneFilter("all");
    setPage(0); setSortOrder(null);
  };

  const sortedContracts = useMemo(() => {
    if (!sortOrder) return contracts;
    return [...contracts].sort((a, b) => {
      const nA = (a.societe || a.party_name || "").toLowerCase();
      const nB = (b.societe || b.party_name || "").toLowerCase();
      return sortOrder === "asc" ? nA.localeCompare(nB, "fr") : nB.localeCompare(nA, "fr");
    });
  }, [contracts, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  // Selection
  const toggleSelectContract = (id) => {
    setSelectedContracts((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAll = () => {
    if (selectedContracts.size === contracts.length) setSelectedContracts(new Set());
    else setSelectedContracts(new Set(contracts.map((c) => c.id)));
  };
  const clearSelection = () => setSelectedContracts(new Set());

  const handleCreateContract = () => { setEditingContract(null); setFormModalOpen(true); };
  const handleEditContract = (c) => { setEditingContract(c); setFormModalOpen(true); };
  const handleDeleteContract = async (c) => {
    const name = c.societe || c.party_name || "ce contrat";
    if (!window.confirm(`Supprimer le contrat de "${name}" ?\n\nCette action supprimera également tous les documents associés.`)) return;
    const reason = window.prompt(
      "Raison de la suppression (facultatif — visible dans la corbeille) :", "");
    setDeleting(c.id);
    try {
      const qs = reason && reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : "";
      await api.delete(`/contracts/${c.id}${qs}`);
      toast.success("Contrat supprimé");
      fetchContracts();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeleting(null); }
  };
  const handleFormSuccess = () => { fetchContracts(); fetchFilters(); };
  const handleImportSuccess = () => { fetchContracts(); fetchFilters(); };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      let toExport = [];
      if (selectedContracts.size > 0) {
        toExport = contracts.filter((c) => selectedContracts.has(c.id));
      } else {
        const p = new URLSearchParams();
        if (search) p.append("search", search);
        if (typeFilter.length) p.append("type", typeFilter.join(","));
        if (etatFilter.length) p.append("etat", etatFilter.join(","));
        if (resiliationFilter.length) p.append("statut_resiliation", resiliationFilter.join(","));
        if (clientFilter.length) p.append("client", clientFilter.join(","));
        if (produitFilter.length) p.append("produit", produitFilter.join(","));
        if (vendeurFilter.length) p.append("vendeur", vendeurFilter.join(","));
        if (revendeurFilter.length) p.append("revendeur", revendeurFilter.join(","));
        if (leaserFilter.length) p.append("leaser", leaserFilter.join(","));
        if (yearFilter && yearFilter !== "all") p.append("year", yearFilter);
        if (gestionInterneFilter && gestionInterneFilter !== "all") p.append("gestion_interne", gestionInterneFilter);
        p.append("skip", "0"); p.append("limit", "10000");
        const r = await api.get(`/contracts?${p.toString()}`);
        toExport = r.data.contracts || [];
      }
      if (toExport.length === 0) { toast.info("Aucun contrat à exporter"); return; }
      const data = toExport.map((c) => ({
        "Société / Client": c.societe || c.party_name || "",
        "Type": c.service_category || c.type || c.contract_type || "",
        "État": c.etat?.label || "",
        "Produit": c.produit || "",
        "Vendeur": c.vendeur || "",
        "Revendeur": c.revendeur || "",
        "Leaser": c.leaser || "",
        "Date Signature": c.date_signature ? new Date(c.date_signature).toLocaleDateString("fr-FR") : "",
        "Date Installation": c.date_installation ? new Date(c.date_installation).toLocaleDateString("fr-FR") : "",
        "Date Fin": (c.date_fin || c.end_date) ? new Date(c.date_fin || c.end_date).toLocaleDateString("fr-FR") : "",
        "Durée (mois)": c.duree_mois || "",
        "MRR (€)": c.montant_abonnement || c.amount_ht || 0,
        "CA Leaser (€)": c.ca_leaser || 0,
        "Gestion Interne (€)": c.abonnement_gestion_interne || 0,
        "Finance Leasing (€)": c.abonnement_finance || 0,
        "N° Financement": c.numero_financement || "",
        "Statut": STATUS_BADGE_CFG[c.status]?.label || c.status || "",
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      ws["!cols"] = [30, 15, 12, 20, 15, 15, 15, 12, 12, 12, 10, 12, 12, 12, 12, 15, 12].map((wch) => ({ wch }));
      XLSX.utils.book_append_sheet(wb, ws, "Contrats");
      const today = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `contrats_export_${today}.xlsx`);
      toast.success(`${toExport.length} contrat(s) exporté(s)`);
    } catch (e) { console.error(e); toast.error("Erreur lors de l'export"); }
    finally { setExporting(false); }
  };

  const visibleColumns = columns.filter((c) => c.visible);
  const hiddenColumns = columns.filter((c) => !c.visible);
  const setColumnVisible = (colId, visible) => {
    const nc = columns.map((c) => c.id === colId ? { ...c, visible } : c);
    setColumns(nc); saveColumnsConfig(nc);
  };

  const renderCell = (c, colId) => {
    switch (colId) {
      case "societe": {
        const name = c.societe || c.party_name || "-";
        return (
          <span className="font-medium text-slate-900 max-w-[200px] truncate cursor-pointer hover:text-blue-600 hover:underline block"
            onClick={() => navigate(`/app/contracts/${c.id}`)} title={name}>
            {name}
          </span>
        );
      }
      case "type": return getTypeBadge(c.service_category || c.type || c.contract_type);
      case "etat": return <EtatIndicator etat={c.etat} />;
      case "produit": return <span className="text-slate-500 text-sm">{c.produit || "-"}</span>;
      case "vendeur": return <span className="text-slate-500 text-sm">{c.vendeur || "-"}</span>;
      case "revendeur": return <span className="text-slate-500 text-sm">{c.revendeur || "-"}</span>;
      case "leaser": return c.leaser && c.leaser !== "NON"
        ? <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">{c.leaser}</Badge>
        : <span className="text-slate-500 text-sm">-</span>;
      case "date_fin": return <span className="text-sm whitespace-nowrap font-mono">{fmtDate(c.date_fin || c.end_date)}</span>;
      case "mrr": return <span className="font-mono text-sm">{fmtEUR(c.montant_abonnement ?? c.amount_ht)}</span>;
      case "ca_leaser": return <span className="font-mono text-sm">{fmtEUR(c.ca_leaser)}</span>;
      case "status": return getStatusBadge(c);
      case "date_installation": return <span className="text-sm whitespace-nowrap font-mono">{fmtDate(c.date_installation)}</span>;
      case "duree_mois": return <span className="text-sm">{c.duree_mois ? `${c.duree_mois} mois` : "-"}</span>;
      case "commission": return <span className="font-mono text-sm">{fmtEUR(c.commission)}</span>;
      case "abonnement_gi": return <span className="font-mono text-sm">{fmtEUR(c.abonnement_gestion_interne)}</span>;
      case "abonnement_fin": return <span className="font-mono text-sm">{fmtEUR(c.abonnement_finance)}</span>;
      case "date_demande_resiliation": return c.date_demande_resiliation
        ? <span className="text-sm whitespace-nowrap">{fmtDate(c.date_demande_resiliation)}</span>
        : <span className="text-slate-400 text-sm">-</span>;
      case "date_effet_resiliation": return c.date_effet_resiliation
        ? <span className={`text-sm whitespace-nowrap font-medium ${(c.statut_resiliation === "resiliation_en_cours" || c.resiliation_status === "en_cours") ? "text-orange-600" : ""}`}>
            {fmtDate(c.date_effet_resiliation)}
          </span>
        : <span className="text-slate-400 text-sm">-</span>;
      default: return "-";
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="contrats-annuaire">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Contrats
          </h1>
          <p className="text-slate-500 text-sm">{total} contrat{total > 1 ? "s" : ""} trouvé{total > 1 ? "s" : ""}</p>
          {gestionInterneFilter !== "all" && totalGestionInterne !== null && (
            <Badge data-testid="total-gestion-interne-badge" className="mt-1 bg-blue-100 text-blue-800 hover:bg-blue-100 font-mono">
              Total Gestion Interne : {fmtEUR(totalGestionInterne)} /mois
            </Badge>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {canWrite && (
            <Button onClick={handleCreateContract} data-testid="new-contract-btn"
              className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Nouveau contrat</span>
              <span className="sm:hidden">Nouveau</span>
            </Button>
          )}
          <Button onClick={() => setShowSemSearch(true)} variant="outline"
            data-testid="semantic-search-open-btn"
            className="flex-1 sm:flex-none border-blue-300 text-blue-700 hover:bg-blue-50">
            <Sparkles className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Recherche IA</span>
            <span className="sm:hidden">IA</span>
          </Button>
          {canWrite && (
            <Button onClick={() => setImportModalOpen(true)} variant="outline"
              data-testid="import-contracts-btn" className="flex-1 sm:flex-none">
              <Upload className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Importer Excel</span>
              <span className="sm:hidden">Importer</span>
            </Button>
          )}
          <Button onClick={handleExportExcel} variant="outline" disabled={exporting || contracts.length === 0}
            data-testid="export-contracts-btn" className="flex-1 sm:flex-none">
            {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            <span className="hidden sm:inline">
              {selectedContracts.size > 0 ? `Exporter (${selectedContracts.size})` : "Exporter Excel"}
            </span>
            <span className="sm:hidden">{selectedContracts.size > 0 ? `(${selectedContracts.size})` : "Exporter"}</span>
          </Button>
          <ContractViewsSelector activeViewId={activeViewId}
            onSelectView={handleSelectView} currentColumns={columns} />
          <ColumnConfigPopover columns={columns} setColumns={setColumns} />
          <Button variant="outline" size="sm" onClick={() => navigate("/app/contracts/corbeille")}
                  data-testid="trash-open-btn" className="text-slate-600">
            <Trash2 className="w-4 h-4 mr-1.5" /> Corbeille
          </Button>
        </div>
      </div>

      {/* Selection toolbar */}
      {selectedContracts.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-900">
              {selectedContracts.size} contrat{selectedContracts.size > 1 ? "s" : ""} sélectionné{selectedContracts.size > 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={toggleSelectAll} className="text-blue-700 hover:text-blue-900 hover:bg-blue-100">
              {selectedContracts.size === contracts.length ? "Tout désélectionner" : "Tout sélectionner"}
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection} className="text-blue-700 hover:text-blue-900 hover:bg-blue-100">
              <X className="w-4 h-4 mr-1" /> Effacer
            </Button>
            <Button size="sm" onClick={handleExportExcel} disabled={exporting} className="bg-blue-600 hover:bg-blue-700 text-white">
              {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Exporter la sélection
            </Button>
            <Button size="sm" onClick={() => setBulkDeleteOpen(true)}
                    data-testid="bulk-delete-open-btn"
                    className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer la sélection
            </Button>
          </div>
        </div>
      )}

      <BulkDeleteContractsDialog
        open={bulkDeleteOpen}
        ids={Array.from(selectedContracts)}
        onOpenChange={setBulkDeleteOpen}
        onDone={() => { clearSelection(); fetchContracts(); }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <form onSubmit={(e) => { e.preventDefault(); setPage(0); fetchContracts(); }} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
              <div className="relative col-span-2 sm:col-span-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Rechercher..." value={search}
                  onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-input" />
              </div>
              <MultiSelectFilter values={typeFilter} onValuesChange={setTypeFilter}
                options={filtersData?.types || []} allLabel="Tous les types" placeholder="Types" data-testid="type-filter" />

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="etat-filter"
                    className={`w-full justify-between h-9 font-normal ${(etatFilter.length + resiliationFilter.length) > 0 ? "border-blue-500 bg-blue-50" : "text-slate-500"}`}>
                    <span className="truncate">
                      {(etatFilter.length + resiliationFilter.length) === 0 ? "État"
                        : (etatFilter.length + resiliationFilter.length) === 1
                          ? (etatFilter.length === 1
                            ? ETAT_OPTIONS.find((o) => o.value === etatFilter[0])?.label
                            : RESILIATION_OPTIONS.find((o) => o.value === resiliationFilter[0])?.label)
                          : `${etatFilter.length + resiliationFilter.length} états`}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase px-2 pt-1">Documents</p>
                    {ETAT_OPTIONS.map((o) => (
                      <div key={o.value}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${etatFilter.includes(o.value) ? "bg-blue-50" : ""}`}
                        onClick={() => {
                          if (etatFilter.includes(o.value)) setEtatFilter(etatFilter.filter((v) => v !== o.value));
                          else setEtatFilter([...etatFilter, o.value]);
                        }}>
                        <Checkbox checked={etatFilter.includes(o.value)} className="pointer-events-none" />
                        <span className={`w-3 h-3 rounded-full ${o.color}`} />
                        <span className="text-sm">{o.label}</span>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500 uppercase px-2 pt-2 border-t mt-2">Résiliation</p>
                    {RESILIATION_OPTIONS.map((o) => (
                      <div key={o.value} data-testid={`resiliation-filter-${o.value}`}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${resiliationFilter.includes(o.value) ? "bg-blue-50" : ""}`}
                        onClick={() => {
                          if (resiliationFilter.includes(o.value)) setResiliationFilter(resiliationFilter.filter((v) => v !== o.value));
                          else setResiliationFilter([...resiliationFilter, o.value]);
                        }}>
                        <Checkbox checked={resiliationFilter.includes(o.value)} className="pointer-events-none" />
                        <span className={`w-3 h-3 rounded-full ${o.color}`} />
                        <span className="text-sm">{o.label}</span>
                      </div>
                    ))}
                    {(etatFilter.length > 0 || resiliationFilter.length > 0) && (
                      <Button variant="ghost" size="sm" className="w-full mt-2 text-xs"
                        onClick={() => { setEtatFilter([]); setResiliationFilter([]); }}>Effacer</Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <MultiSelectFilter values={produitFilter} onValuesChange={setProduitFilter}
                options={filtersData?.produits || []} allLabel="Tous les produits" placeholder="Produits" data-testid="produit-filter" />
              <MultiSelectFilter values={clientFilter} onValuesChange={setClientFilter}
                options={filtersData?.clients || []} allLabel="Tous les clients" placeholder="Clients" data-testid="client-filter" />
              <Select value={gestionInterneFilter} onValueChange={setGestionInterneFilter}>
                <SelectTrigger data-testid="gestion-interne-filter"
                  className={`h-9 font-normal ${gestionInterneFilter !== "all" ? "border-blue-500 bg-blue-50" : "text-slate-500"}`}>
                  <SelectValue placeholder="Gestion Interne" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Gestion Interne : Tous</SelectItem>
                  <SelectItem value="avec">Avec abonnement (&gt; 0 €)</SelectItem>
                  <SelectItem value="sans">Sans abonnement (0 €)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
              <MultiSelectFilter values={vendeurFilter} onValuesChange={setVendeurFilter}
                options={filtersData?.vendeurs || []} allLabel="Tous les vendeurs" placeholder="Vendeurs" data-testid="vendeur-filter" />
              <MultiSelectFilter values={revendeurFilter} onValuesChange={setRevendeurFilter}
                options={filtersData?.revendeurs || []} allLabel="Tous les revendeurs" placeholder="Revendeurs" data-testid="revendeur-filter" />
              <MultiSelectFilter values={leaserFilter} onValuesChange={setLeaserFilter}
                options={filtersData?.leasers || []} allLabel="Tous les leasers" placeholder="Leasers" data-testid="leaser-filter" />
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger data-testid="year-filter"><SelectValue placeholder="Années" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les années</SelectItem>
                  {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={resetFilters} className="flex-1 sm:flex-none">
                  <Filter className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Réinitialiser</span>
                  <span className="sm:hidden">Réinit.</span>
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={fetchContracts} className="flex-1 sm:flex-none">
                  <RefreshCw className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Actualiser</span>
                </Button>
              </div>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                <Search className="w-4 h-4 mr-2" /> Rechercher
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 sm:p-6 space-y-3">
              {[...Array(10)].map((_, i) => <div key={i} className="h-12 bg-slate-100 animate-pulse rounded" />)}
            </div>
          ) : contracts.length === 0 ? (
            <div className="p-8 sm:p-12 text-center" data-testid="empty-state">
              <FileText className="w-10 sm:w-12 h-10 sm:h-12 mx-auto text-slate-400 mb-2" />
              <p className="text-slate-500">Aucun contrat trouvé</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="block lg:hidden divide-y">
                {sortedContracts.map((c, i) => (
                  <div key={c.id || i}
                    className={`p-3 hover:bg-slate-50 cursor-pointer ${selectedContracts.has(c.id) ? "bg-blue-50" : ""}`}
                    onClick={() => navigate(`/app/contracts/${c.id}`)}
                    data-testid={`contract-card-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <Checkbox checked={selectedContracts.has(c.id)}
                          onCheckedChange={() => toggleSelectContract(c.id)}
                          onClick={(e) => e.stopPropagation()} className="mt-1" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">{c.societe || c.party_name}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {getTypeBadge(c.service_category || c.type || c.contract_type)}
                            {getStatusBadge(c)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-mono font-bold text-sm">{fmtEUR(c.montant_abonnement ?? c.amount_ht)}</p>
                        <p className="text-xs text-slate-500 font-mono">{fmtDate(c.date_fin || c.end_date)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden lg:block overflow-x-auto">
                <Table className="min-w-[900px]">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-12">
                        <Checkbox
                          checked={contracts.length > 0 && selectedContracts.size === contracts.length}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Sélectionner tout"
                          data-testid="select-all-checkbox" />
                      </TableHead>
                      {visibleColumns.map((col) => (
                        <TableHead key={col.id} data-testid={`th-${col.id}`}
                          className={`text-xs uppercase text-slate-500 font-medium whitespace-nowrap select-none p-0 group transition-all
                            ${dragOverColId === col.id ? "bg-blue-100" : ""}`}>
                          <div draggable
                            onDragStart={(e) => handleHeaderDragStart(e, col.id)}
                            onDragOver={(e) => handleHeaderDragOver(e, col.id)}
                            onDragLeave={handleHeaderDragLeave}
                            onDrop={(e) => handleHeaderDrop(e, col.id)}
                            onDragEnd={handleHeaderDragEnd}
                            title="Glisser pour réorganiser"
                            className={`h-10 px-2 flex items-center gap-1 cursor-move
                              ${col.align === "right" ? "justify-end" : ""}
                              ${draggedColId === col.id ? "opacity-40" : ""}`}
                            data-testid={`drag-handle-${col.id}`}>
                            <GripVertical className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" />
                            {col.id === "societe" ? (
                              <Button variant="ghost" size="sm"
                                className="h-auto p-0 hover:bg-transparent font-medium text-xs uppercase text-slate-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSortOrder((o) => o === null ? "asc" : o === "asc" ? "desc" : null);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                draggable={false} data-testid="sort-by-name-btn">
                                {col.label}
                                {sortOrder === "asc" && <ArrowUpAZ className="w-4 h-4 ml-1 text-blue-600" />}
                                {sortOrder === "desc" && <ArrowDownAZ className="w-4 h-4 ml-1 text-blue-600" />}
                                {sortOrder === null && <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />}
                              </Button>
                            ) : col.label}
                            <button type="button"
                              onClick={(e) => { e.stopPropagation(); setColumnVisible(col.id, false); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              draggable={false}
                              className="ml-1 opacity-0 group-hover:opacity-70 hover:!opacity-100 hover:text-red-600 transition-opacity flex-shrink-0"
                              title="Masquer cette colonne"
                              data-testid={`hide-column-${col.id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </TableHead>
                      ))}
                      {hiddenColumns.length > 0 && (
                        <TableHead className="w-8 p-0">
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button"
                                className="h-10 w-8 flex items-center justify-center text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition"
                                title="Ajouter une colonne" data-testid="add-column-btn">
                                <Plus className="w-4 h-4" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-1" align="end">
                              <p className="text-xs font-medium text-slate-500 px-2 py-1.5 uppercase tracking-wider">Colonnes masquées</p>
                              <div className="max-h-72 overflow-y-auto">
                                {hiddenColumns.map((col) => (
                                  <button key={col.id} type="button"
                                    onClick={() => setColumnVisible(col.id, true)}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-blue-50 rounded transition text-left"
                                    data-testid={`show-column-${col.id}`}>
                                    <Plus className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                    <span className="flex-1">{col.label}</span>
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </TableHead>
                      )}
                      <TableHead className="text-xs uppercase text-slate-500 font-medium text-right whitespace-nowrap">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedContracts.map((c, i) => (
                      <TableRow key={c.id || i} data-testid={`contract-row-${i}`}
                        className={`hover:bg-slate-50 ${selectedContracts.has(c.id) ? "bg-blue-50" : ""}`}>
                        <TableCell className="w-12">
                          <Checkbox checked={selectedContracts.has(c.id)}
                            onCheckedChange={() => toggleSelectContract(c.id)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Sélectionner ${c.societe || c.party_name}`}
                            data-testid={`select-contract-${i}`} />
                        </TableCell>
                        {visibleColumns.map((col) => (
                          <TableCell key={col.id} className={col.align === "right" ? "text-right" : ""}>
                            {renderCell(c, col.id)}
                          </TableCell>
                        ))}
                        {hiddenColumns.length > 0 && <TableCell className="w-8" />}
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm"
                              onClick={() => navigate(`/app/contracts/${c.id}`)}
                              title="Voir les détails" data-testid={`open-detail-${c.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {canWrite && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleEditContract(c)}>
                                    <Pencil className="w-4 h-4 mr-2" /> Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDeleteContract(c)}
                                    className="text-red-600" disabled={deleting === c.id}>
                                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-4 border-t gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 hidden sm:inline">Afficher</span>
                  <Select value={perPage.toString()} onValueChange={(v) => { setPerPage(parseInt(v)); setPage(0); }}>
                    <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-slate-500 hidden sm:inline">par page</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">
                    <span className="hidden sm:inline">Page </span>{page + 1}/{totalPages}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ImportContractsModal isOpen={importModalOpen} onClose={() => setImportModalOpen(false)} onSuccess={handleImportSuccess} kind="contracts" />
      <ContractSemanticSearchDialog open={showSemSearch} onOpenChange={setShowSemSearch} />
      <ContractFormModal open={formModalOpen} onOpenChange={setFormModalOpen}
        contract={editingContract} onSuccess={handleFormSuccess} filters={filtersData} />
    </div>
  );
}
